import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api } from "~/api/client";
import { useAuthStore } from "~/ui/auth/authStore";
import { dateCompare, naturalCompare } from "~/domain/sorting";

interface PendingRegistration {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string | null;
  message: string | null;
  status: string;
  created_at: string;
}

export function AdminPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const queryClient = useQueryClient();
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("date-desc");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-registrations"],
    queryFn: () =>
      api.get<{ registrations: PendingRegistration[] }>("/admin/registrations"),
    enabled: isAdmin,
  });

  const actionMutation = useMutation({
    mutationFn: ({
      id,
      action,
      password,
    }: {
      id: string;
      action: "approve" | "reject";
      password?: string;
    }) => api.put(`/admin/registrations/${id}`, { action, password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-registrations"] });
      setApprovingId(null);
      setTempPassword("");
    },
  });

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-red-600">Zugriff verweigert.</p>
      </div>
    );
  }

  const registrations = (data?.registrations ?? [])
    .filter(
      (registration) =>
        (statusFilter === "all" || registration.status === statusFilter) &&
        `${registration.first_name} ${registration.last_name} ${registration.email} ${registration.company_name ?? ""}`
          .toLocaleLowerCase("de")
          .includes(search.toLocaleLowerCase("de")),
    )
    .sort((left, right) => {
      if (sort === "date-asc") return dateCompare(left.created_at, right.created_at);
      if (sort === "name-asc") return naturalCompare(`${left.last_name} ${left.first_name}`, `${right.last_name} ${right.first_name}`);
      if (sort === "name-desc") return naturalCompare(`${right.last_name} ${right.first_name}`, `${left.last_name} ${left.first_name}`);
      return dateCompare(right.created_at, left.created_at);
    });
  const pending = registrations.filter((r) => r.status === "pending");
  const processed = registrations.filter((r) => r.status !== "pending");

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Admin</h1>
        <button
          onClick={() => navigate("/app/customers")}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          ← Zurück zur App
        </button>
      </div>
      <div className="mb-6 flex flex-wrap gap-3">
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, E-Mail oder Unternehmen suchen…" className="min-w-64 flex-1 rounded-md border px-3 py-2" />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-md border px-3 py-2">
          <option value="all">Alle Status</option>
          <option value="pending">Ausstehend</option>
          <option value="approved">Freigegeben</option>
          <option value="rejected">Abgelehnt</option>
        </select>
        <select value={sort} onChange={(event) => setSort(event.target.value)} className="rounded-md border px-3 py-2">
          <option value="date-desc">Neueste zuerst</option>
          <option value="date-asc">Älteste zuerst</option>
          <option value="name-asc">Name A–Z</option>
          <option value="name-desc">Name Z–A</option>
        </select>
      </div>

      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Ausstehende Anfragen ({pending.length})
        </h2>

        {isLoading ? (
          <p className="text-gray-500">Laden...</p>
        ) : pending.length === 0 ? (
          <p className="text-gray-500">Keine ausstehenden Anfragen.</p>
        ) : (
          <div className="space-y-4">
            {pending.map((reg) => (
              <div
                key={reg.id}
                className="rounded-lg border border-gray-200 bg-white p-6"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {reg.first_name} {reg.last_name}
                    </p>
                    <p className="text-sm text-gray-600">{reg.email}</p>
                    {reg.company_name && (
                      <p className="text-sm text-gray-600">
                        🏢 {reg.company_name}
                      </p>
                    )}
                    {reg.message && (
                      <p className="mt-2 text-sm italic text-gray-600">
                        "{reg.message}"
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      {new Date(reg.created_at).toLocaleString("de-DE")}
                    </p>
                  </div>
                </div>

                {approvingId === reg.id ? (
                  <div className="mt-4 rounded-md bg-blue-50 p-4">
                    <p className="mb-2 text-sm font-medium text-gray-700">
                      Temporäres Passwort für {reg.first_name}:
                    </p>
                    <input
                      type="text"
                      value={tempPassword}
                      onChange={(e) => setTempPassword(e.target.value)}
                      placeholder="Mindestens 12 Zeichen"
                      className="mb-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          actionMutation.mutate({
                            id: reg.id,
                            action: "approve",
                            password: tempPassword,
                          })
                        }
                        disabled={
                          tempPassword.length < 12 || actionMutation.isPending
                        }
                        className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        Bestätigen & Konto erstellen
                      </button>
                      <button
                        onClick={() => {
                          setApprovingId(null);
                          setTempPassword("");
                        }}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => setApprovingId(reg.id)}
                      className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                    >
                      Genehmigen
                    </button>
                    <button
                      onClick={() =>
                        actionMutation.mutate({ id: reg.id, action: "reject" })
                      }
                      disabled={actionMutation.isPending}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Ablehnen
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {processed.length > 0 && (
        <div>
          <h2 className="mb-4 text-xl font-semibold text-gray-900">
            Bearbeitet ({processed.length})
          </h2>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    E-Mail
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Datum
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {processed.map((reg) => (
                  <tr key={reg.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {reg.first_name} {reg.last_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {reg.email}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          reg.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {reg.status === "approved" ? "Genehmigt" : "Abgelehnt"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(reg.created_at).toLocaleDateString("de-DE")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
